/**
 * @fileoverview 고객센터에서 사용되는 상수 및 설정 정의
 * 게시판, 파일 업로드, 연락/문의 상태 관련 설정
 */

/**
 * 게시판 설정 인터페이스
 * @interface BoardConfig
 * @property {'list' | 'view'} type - 게시판 타입 (목록 또는 상세)
 * @property {string} boardAlias - 게시판 별칭
 */
interface BoardConfig {
  type: "list" | "view";
  boardAlias: string;
}

/**
 * 상태 필터 인터페이스
 * @interface StatusFilter
 * @property {number} filterId - 필터 ID
 * @property {string} filterName - 필터 이름
 */
interface StatusFilter {
  filterId: number;
  filterName: string;
}

/**
 * 외부 리소스 URL 반환 함수
 * 게시판 기능에 필요한 JavaScript 파일과 CSS 파일의 CDN URL을 정의합니다.
 * @constant {Object} RESOURCE_URLS
 * @property {string} script - 게시판 생성용 JavaScript 파일 URL
 * @property {string} communityCss - 커뮤니티 스타일 CSS 파일 URL
 */
export const getResourceUrls = (assetsDomain: string = "https://assets.playnccdn.com", communityVersion: string = "2.3.6") => {
  return {
    script: `${assetsDomain}/uikit/uikit-nccommunity/${communityVersion}/js/createBoard.js`,
    communityCss: `${assetsDomain}/uikit/uikit-nccommunity/${communityVersion}/css/community.css`,
  };
};

/**
 * 기본 게시판 설정 파라미터
 * @constant {Object} BASE_BOARD_CONFIG
 * @property {string} service - 서비스 이름
 * @property {string} alias - 설정 별칭
 * @property {string} lang - 언어 코드
 */
const BASE_BOARD_CONFIG = {
  service: "ncfamilyzone",
  alias: "ncfz_boardConfig",
  lang: "ko-KR",
} as const;

/**
 * 게시판 타입 상수
 * @constant {Object} BOARD_TYPES
 * @property {string} LIST - 목록 타입
 * @property {string} VIEW - 상세 보기 타입
 */
const BOARD_TYPES = {
  LIST: "list",
  VIEW: "view",
} as const;

/**
 * 게시판 별칭 상수
 * @constant {Object} BOARD_ALIASES
 * @property {string} NOTICE - 공지사항 별칭
 * @property {string} FAQ - FAQ 별칭
 */
const BOARD_ALIASES = {
  NOTICE: "notice",
  FAQ: "faq",
} as const;

/**
 * 게시판 설정 파라미터 (BASE_BOARD_CONFIG와 동일)
 * API 호출 시 사용되는 기본 파라미터입니다.
 * @constant {Object} BOARD_CONFIG_PARAMS
 */
export const BOARD_CONFIG_PARAMS = BASE_BOARD_CONFIG;

/**
 * 공지사항 목록 게시판 설정
 * @constant {BoardConfig} NOTICE_BOARD_CONFIG
 */
export const NOTICE_BOARD_CONFIG: BoardConfig = {
  type: BOARD_TYPES.LIST,
  boardAlias: BOARD_ALIASES.NOTICE,
} as const;

/**
 * 공지사항 상세 보기 게시판 설정
 * @constant {BoardConfig} NOTICE_BOARD_VIEW_CONFIG
 */
export const NOTICE_BOARD_VIEW_CONFIG: BoardConfig = {
  type: BOARD_TYPES.VIEW,
  boardAlias: BOARD_ALIASES.NOTICE,
} as const;

/**
 * FAQ 목록 게시판 설정
 * @constant {BoardConfig} FAQ_BOARD_CONFIG
 */
export const FAQ_BOARD_CONFIG: BoardConfig = {
  type: BOARD_TYPES.LIST,
  boardAlias: BOARD_ALIASES.FAQ,
} as const;

/**
 * FAQ 상세 보기 게시판 설정
 * @constant {BoardConfig} FAQ_BOARD_VIEW_CONFIG
 */
export const FAQ_BOARD_VIEW_CONFIG: BoardConfig = {
  type: BOARD_TYPES.VIEW,
  boardAlias: BOARD_ALIASES.FAQ,
} as const;

/**
 * TOP FAQ API 요청 매개변수
 */
export const TOP_FAQS_PARAMS = {
  service: "ncfamilyzone",
  alias: "ncfz_topfaqs",
  lang: "ko-KR",
} as const;

/**
 * 파일 업로드 관련 설정
 * 1:1 문의에서 파일 첨부 시 사용되는 제한사항과 개인정보 처리방침을 정의합니다.
 * @constant {Object} FILE_UPLOAD
 * @property {number} MAX_SIZE - 최대 파일 크기 (10MB)
 * @property {string[]} ACCEPTED_TYPES - 허용되는 파일 MIME 타입 목록
 * @property {string} PRIVACY_POLICY_CONTENT - 개인정보 수집 및 이용동의 HTML 내용
 */
export const FILE_UPLOAD = {
  /** 첨부 가능한 최대 파일 크기 (10MB) */
  MAX_SIZE: 10 * 1024 * 1024,

  /** 첨부 가능한 파일 종류 */
  ACCEPTED_TYPES: [
    "text/plain", // txt
    "application/vnd.ms-excel", // xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
    "application/msword", // doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
    "application/vnd.ms-powerpoint", // ppt, pps
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
    "application/vnd.openxmlformats-officedocument.presentationml.slideshow", // ppsx
    // HWP 파일을 위한 다양한 MIME 타입 추가
    "application/x-hwp", // hwp (기본)
    "application/haansofthwp", // hwp (한소프트)
    "application/vnd.hancom.hwp", // hwp (대체)
    "application/pdf", // pdf
    "image/jpeg", // jpg, jpeg
    "image/bmp", // bmp
    "image/gif", // gif
    "image/png", // png
    // ZIP 파일을 위한 다양한 MIME 타입 추가
    "application/zip", // zip (기본)
    "application/x-zip-compressed", // zip (대체)
    // ALZ 파일을 위한 MIME 타입 (거의 인식되지 않음)
    "application/x-alz", // alz
    "application/x-alzip", // alz (대체)
    // RAR 파일을 위한 다양한 MIME 타입 추가
    "application/x-rar-compressed", // rar (기본)
    "application/vnd.rar", // rar (표준)
    "application/x-rar", // rar (대체)
  ],

  /** 허용되는 파일 확장자 (MIME 타입 검증 실패시 대체용) */
  ACCEPTED_EXTENSIONS: [".txt", ".xls", ".xlsx", ".doc", ".docx", ".ppt", ".pps", ".pptx", ".ppsx", ".hwp", ".pdf", ".jpg", ".jpeg", ".bmp", ".gif", ".png", ".zip", ".alz", ".rar"],

  /** 첨부 가능한 이미지 종류 */
  ACCEPTED_IMAGE_TYPES: [
    "image/jpg", // jpg,
    "image/jpeg", // jpg, jpeg
    "image/png", // png
  ],
} as const;

/**
 * 개인 정보 수집 및 이용동의 내용
 * @constant {string} PRIVACY_POLICY_CONTENT
 */
export const PRIVACY_POLICY_CONTENT = `
    <p>
      <strong>[1:1문의를 위한 개인정보 수집 및 이용동의]</strong>
      <br />
      ㈜엔씨소프트는 비회원(또는 회원 승인 대기 고객)의 1:1문의를 위해 아래와 같이 고객님의 개인정보를 수집∙이용하고 있습니다.
      <br />
      - 수집항목 : 성명, 연락처(유선전화 혹은 휴대전화), 이메일주소
      <br />
      - 수집목적 : 고객문의 처리를 위한 상담
      <br />- 보유 및 이용 기간 : <strong style="color: #0800ff;">문의 처리 후 전자상거래법에 따라 3년간 보관 후 파기</strong>
      <br />※ 고객님은 개인정보 수집·이용에 대한 동의를 거부하실 권리가 있으며, 동의를 거부하실 경우 1:1문의를 통한 상담 서비스가 제공되지 않습니다.
    </p>
  ` as const;

/**
 * 연락/문의 진행 상태 열거형
 * 1:1 문의 및 신고의 처리 상태를 나타냅니다.
 * @enum {number} CONTACT_STATUS
 */
export enum CONTACT_STATUS {
  /** 처리 완료 */
  reportCompleted = 1,
  /** 취하 완료 */
  withdrawCompleted = 2,
  /** 신고 접수 */
  reportReceived = 3,
  /** 취하 접수 */
  withdrawPending = 4,
  /** 처리 불가 */
  reportRejected = 5,
  /** 답변 완료 */
  answered = 6,
  /** 답변 대기 */
  pendingAnswer = 7,
}

/**
 * 연락 상태 변형 타입 (enum의 키 타입)
 * @typedef {keyof typeof CONTACT_STATUS} ContactStatusVariant
 */
export type ContactStatusVariant = keyof typeof CONTACT_STATUS;

/**
 * 연락 상태 값 타입 (enum의 값 타입)
 * @typedef {CONTACT_STATUS} ContactStatusValue
 */
export type ContactStatusValue = CONTACT_STATUS;

/**
 * 연락/문의 관련 설정
 * 상태 필터와 상태 코드 목록을 그룹화합니다.
 * @constant {Object} CONTACT
 * @property {readonly StatusFilter[]} STATUS_FILTERS - 상태 필터 목록 (전체, 답변대기, 답변완료)
 * @property {readonly StatusFilter[]} STATUS_CODES - 모든 상태 코드 목록
 */
export const CONTACT = {
  /** 상태 필터 목록 (사용자 인터페이스용) */
  STATUS_FILTERS: [
    { filterId: 0, filterName: "전체" },
    { filterId: CONTACT_STATUS.pendingAnswer, filterName: "답변대기" },
    { filterId: CONTACT_STATUS.answered, filterName: "답변완료" },
  ] as const satisfies readonly StatusFilter[],

  /** 모든 상태 코드 목록 (관리자용) */
  STATUS_CODES: [
    { filterId: NaN, filterName: "진행상태별 전체" },
    { filterId: CONTACT_STATUS.reportCompleted, filterName: "처리완료" },
    { filterId: CONTACT_STATUS.withdrawCompleted, filterName: "취하완료" },
    { filterId: CONTACT_STATUS.reportReceived, filterName: "신고접수" },
    { filterId: CONTACT_STATUS.withdrawPending, filterName: "취하접수" },
    { filterId: CONTACT_STATUS.reportRejected, filterName: "처리불가" },
  ] as const satisfies readonly StatusFilter[],
} as const;

/**
 * 계정 상태 열거형
 * PC방 계정의 승인 상태를 나타냅니다.
 * @enum {number} ACCOUNT_STATUS
 */
export enum ACCOUNT_STATUS {
  /** 신청 전 */
  NotApplied,
  /** 전체 대기 */
  PendingAll,
  /** 일부 승인 */
  PartialApproved,
  /** 전체 승인 */
  ApprovedAll,
}
